import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { inject, NgZone, Injector, Component, ChangeDetectionStrategy, ViewEncapsulation, ViewChild, Input, Output, Injectable } from '@angular/core';
import { arrow, flip, computePosition, offset, shift, size, hide, autoUpdate } from '@floating-ui/dom';
import { CoreModule } from '@odx/angular';
import { DynamicViewDirective, DynamicViewService } from '@odx/angular/cdk/dynamic-view';
import { CSSComponent, deepmerge } from '@odx/angular/internal';
import { px, getSide, getOppositeSide, applyStyles, Position, waitForAnimations, injectElement, deferFn, hasChanged, isPresent } from '@odx/angular/utils';
import { EMPTY, Subject } from 'rxjs';

/**
 * Computes the position of an overlay element relative to a reference element.
 *
 * @param referenceElement - The reference element to position the overlay relative to.
 * @param overlayElement - The overlay element to be positioned.
 * @param contentElement - The content element within the overlay.
 * @param arrowElement - The arrow element within the overlay.
 * @param options - The options for positioning the overlay.
 * @returns A promise that resolves to the computed position of the overlay.
 */
async function computeOverlayPosition(referenceElement, overlayElement, contentElement, arrowElement, options) {
    const variablePrefix = '--odx-cdk-connected-overlay';
    const { position, offset: offset$1, outerPadding, matchReferenceWidth, enableFallback, fallbackAxisSideDirection, strategy, minHeight } = options;
    const arrowSize = options.showArrow ? arrowElement.offsetWidth : 0;
    const arrowMiddleware = options.showArrow ? [arrow({ element: arrowElement, padding: 8 })] : [];
    const flipMiddleWare = enableFallback ? [flip({ fallbackAxisSideDirection })] : [];
    return computePosition(referenceElement, overlayElement, {
        strategy,
        placement: position,
        middleware: [
            offset(Math.max(offset$1, arrowSize + offset$1)),
            shift({ padding: outerPadding }),
            ...flipMiddleWare,
            size({
                padding: outerPadding,
                apply: ({ availableHeight, rects }) => {
                    contentElement.style.setProperty?.(`${variablePrefix}-min-height`, minHeight ? px(Math.min(availableHeight, minHeight)) : null);
                    contentElement.style.setProperty?.(`${variablePrefix}-min-width`, matchReferenceWidth ? px(rects.reference.width) : null);
                },
            }),
            ...arrowMiddleware,
            hide({ rootBoundary: 'viewport' }),
        ],
    }).then((result) => {
        overlayElement.style.setProperty?.(`${variablePrefix}-position-x`, px(result.x));
        overlayElement.style.setProperty?.(`${variablePrefix}-position-y`, px(result.y));
        if (options.hiddenClass) {
            overlayElement.classList.toggle(options.hiddenClass, !!result.middlewareData.hide?.referenceHidden);
        }
        if (options.showArrow) {
            const overlaySide = getSide(result.placement);
            const arrowSide = getOppositeSide(result.placement);
            applyStyles(arrowElement, {
                display: null,
                top: px(result.middlewareData.arrow?.y),
                left: px(result.middlewareData.arrow?.x),
                [arrowSide]: px(-arrowSize / 2),
                [overlaySide]: null,
            });
        }
        else {
            applyStyles(arrowElement, { display: 'none' });
        }
        return result;
    });
}

const DefaultConnectedOverlayOptions = {
    hiddenClass: 'is-hidden',
    enableFallback: false,
    fallbackAxisSideDirection: 'start',
    matchReferenceWidth: false,
    offset: 8,
    outerPadding: 12,
    position: Position.BOTTOM,
    strategy: 'fixed',
};

/**
 * A reference to a connected overlay created by the `ConnectedOverlayService`. This class provides methods to interact
 * with the overlay, such as updating its options or closing it. It encapsulates the `DynamicViewRef` for the overlay's instance,
 * allowing for direct manipulation of the overlay's state and behavior.
 *
 * This example demonstrates how to use a `ConnectedOverlayRef` to update and close a connected overlay in response
 * to user interactions.
 *
 * @example
 * ```ts
 * class MyComponent {
 *   private overlayRef?: ConnectedOverlayRef;
 *
 *   constructor(private overlayService: ConnectedOverlayService) {}
 *
 *   openOverlay() {
 *     this.overlayRef = this.overlayService.createOverlay(this.someElementRef.nativeElement, { position: 'bottom' });
 *   }
 *
 *   updateOverlayPosition() {
 *     if (this.overlayRef) {
 *       this.overlayRef.update({ position: 'top' });
 *     }
 *   }
 *
 *   closeOverlay() {
 *     this.overlayRef?.close();
 *   }
 * }
 * ```
 *
 * In this example, `MyComponent` uses the `ConnectedOverlayService` to create an overlay. It stores a reference to
 * the created overlay in `overlayRef`, allowing it to update the overlay's position or close it when necessary.
 */
class ConnectedOverlayRef {
    constructor(dynamicViewRef) {
        this.dynamicViewRef = dynamicViewRef;
        this.element = this.dynamicViewRef.getElement();
        /**
         * An Observable that emits when the overlay is closed.
         *
         * @emits {void} - Emits when the overlay is closed.
         *
         */
        this.onClose$ = this.dynamicViewRef.getContext().closed ?? EMPTY;
    }
    /**
     * Updates the connected overlay with new options.
     *
     * @param options {Partial<ConnectedOverlayOptions>} - The new options to be applied to the connected overlay.
     */
    update(options) {
        this.dynamicViewRef.update({ options: options });
    }
    /**
     * Closes the connected overlay.
     *
     * @returns {void}
     */
    async close(force = false) {
        const hiddenClass = force ? 'force-hidden' : this.dynamicViewRef.getContext().options?.hiddenClass || 'is-hidden';
        this.element?.classList.add(hiddenClass);
        await waitForAnimations(this.element);
        this.dynamicViewRef.destroy();
    }
}

let ConnectedOverlayComponent = class ConnectedOverlayComponent {
    constructor() {
        this.zone = inject(NgZone);
        this.positionUpdater = null;
        this.injector = inject(Injector);
        this.element = injectElement();
        this.options = DefaultConnectedOverlayOptions;
        this.closed = new Subject();
    }
    ngOnInit() {
        this.options.hooks?.beforeOpen?.();
        deferFn(() => {
            this.element.nativeElement?.showPopover?.();
            this.options.hooks?.afterOpen?.();
        });
    }
    ngOnChanges(changes) {
        if (hasChanged(changes, ['options'], false)) {
            this.overlaySide = getSide(this.options.position);
            this.element.nativeElement.classList.toggle('odx-cdk-connected-overlay--non-interactive', this.options.nonInteractive ?? false);
        }
        if (hasChanged(changes, ['referenceElement', 'options'], false)) {
            this.stopPositionUpdater();
            this.startPositionUpdater();
        }
    }
    ngOnDestroy() {
        this.options.hooks?.beforeClose?.();
        this.stopPositionUpdater();
        this.element.nativeElement?.hidePopover?.();
        this.closed.next();
        this.options.hooks?.afterClose?.();
    }
    startPositionUpdater() {
        this.positionUpdater = this.zone.runOutsideAngular(() => autoUpdate(this.referenceElement, this.element.nativeElement, () => this.updatePosition(), {
            elementResize: isPresent(window.ResizeObserver),
            animationFrame: this.options.updateOnAnimationFrame,
        }));
    }
    stopPositionUpdater() {
        this.positionUpdater?.();
        this.positionUpdater = null;
    }
    async updatePosition() {
        return computeOverlayPosition(this.referenceElement, this.element.nativeElement, this.contentElement.nativeElement, this.arrowElement.nativeElement, this.options).then(({ placement }) => {
            this.overlaySide = getSide(placement);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ConnectedOverlayComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: ConnectedOverlayComponent, isStandalone: true, selector: "odx-connected-overlay[popover=\"manual\"]", inputs: { referenceElement: "referenceElement", options: "options" }, outputs: { closed: "closed" }, viewQueries: [{ propertyName: "contentElement", first: true, predicate: ["content"], descendants: true, static: true }, { propertyName: "arrowElement", first: true, predicate: ["arrow"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"odx-cdk-connected-overlay__content {{ options.containerClass }}\" #content>\n  <ng-template [odxDynamicView]=\"options.content\" [odxDynamicViewContext]=\"options.context\" [odxDynamicViewInjector]=\"injector\" />\n  <div class=\"odx-cdk-connected-overlay-arrow\" #arrow></div>\n</div>\n", dependencies: [{ kind: "ngmodule", type: CoreModule }, { kind: "directive", type: DynamicViewDirective, selector: "ng-template[odxDynamicView]", inputs: ["odxDynamicView", "odxDynamicViewInjector", "odxDynamicViewContext"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
ConnectedOverlayComponent = __decorate([
    CSSComponent('cdk-connected-overlay')
], ConnectedOverlayComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ConnectedOverlayComponent, decorators: [{
            type: Component,
            args: [{ standalone: true, selector: 'odx-connected-overlay[popover="manual"]', imports: [CoreModule, DynamicViewDirective], changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "<div class=\"odx-cdk-connected-overlay__content {{ options.containerClass }}\" #content>\n  <ng-template [odxDynamicView]=\"options.content\" [odxDynamicViewContext]=\"options.context\" [odxDynamicViewInjector]=\"injector\" />\n  <div class=\"odx-cdk-connected-overlay-arrow\" #arrow></div>\n</div>\n" }]
        }], propDecorators: { contentElement: [{
                type: ViewChild,
                args: ['content', { static: true }]
            }], arrowElement: [{
                type: ViewChild,
                args: ['arrow', { static: true }]
            }], referenceElement: [{
                type: Input
            }], options: [{
                type: Input
            }], closed: [{
                type: Output
            }] } });

/**
 * Service for creating and managing connected overlays. These overlays are UI elements such as popovers, tooltips,
 * or dropdown menus that are positioned relative to a reference element in the DOM. This service leverages the `DynamicViewService`
 * for dynamic component rendering and provides a streamlined API for creating overlays with custom positioning and options.
 *
 * Use the `ConnectedOverlayService` to dynamically create an overlay next to a specific element in your Angular application.
 * This example demonstrates creating a connected overlay next to a button element when the button is clicked.
 *
 * @example
 * ```ts
 * import { Component, ElementRef, ViewChild } from '@angular/core';
 * import { ConnectedOverlayService, ConnectedOverlayOptions } from '@odx/angular/cdk/connected-overlay';
 *
 * @Component({
 *   selector: 'app-example',
 *   template: `<button #triggerElement>Click me</button>`,
 * })
 * class AppComponent {
 *   @ViewChild('triggerElement') triggerElementRef!: ElementRef;
 *
 *   constructor(private connectedOverlayService: ConnectedOverlayService) {}
 *
 *   showOverlay(): void {
 *     const options: Partial<ConnectedOverlayOptions> = {
 *       position: 'bottom-start',
 *       showArrow: true,
 *       content: 'Hello, world!',
 *     };
 *
 *     this.connectedOverlayService.createOverlay(this.triggerElementRef.nativeElement, options);
 *   }
 * }
 * ```
 *
 * This service simplifies the process of creating dynamic, positionally aware overlays that enhance the interactivity
 * and visual appeal of Angular applications.
 */
class ConnectedOverlayService {
    constructor() {
        this.dynamicViewService = inject(DynamicViewService);
    }
    /**
     * Creates a connected overlay.
     * @param referenceElement - The reference element to which the overlay will be connected.
     * @param options - Optional configuration for the connected overlay.
     * @param renderingOptions - Optional rendering options for the connected overlay.
     * @returns {ConnectedOverlayRef} The created Connected Overlay reference instance.
     */
    createOverlay(referenceElement, options, renderingOptions) {
        const dynamicViewRef = this.dynamicViewService.createView(ConnectedOverlayComponent, {
            ...renderingOptions,
            context: { options: deepmerge(DefaultConnectedOverlayOptions, options), referenceElement },
        });
        return new ConnectedOverlayRef(dynamicViewRef);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ConnectedOverlayService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ConnectedOverlayService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ConnectedOverlayService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ConnectedOverlayComponent, ConnectedOverlayRef, ConnectedOverlayService, DefaultConnectedOverlayOptions };
